import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Product Disclosures page component (California AB1200 is the first regime).
 *
 * The disclosure list is sourced from `product_disclosure` metaobjects, which
 * the storefront search backend cannot index. So instead of querying search,
 * this component:
 *   1. Reads the server-rendered rows (the first 250-entry paginate chunk).
 *   2. Loads any remaining chunks once via the Section Rendering API and appends
 *      their rows, so the whole catalog lives in the DOM.
 *   3. Takes over from the no-JS server pagination and filters + paginates
 *      entirely client-side (show/hide), matching SKU or product name.
 *
 * Without JavaScript the full list still renders server-side (paged in
 * 250-entry chunks) — complete, just not live-filterable.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} liveRegion
 * @property {HTMLUListElement} [results]
 * @property {HTMLElement} [serverPagination]
 * @property {HTMLElement} [clientPagination]
 * @property {HTMLElement} [noMatches]
 * @extends {Component<Refs>}
 */
class ProductDisclosuresComponent extends Component {
  requiredRefs = ['searchInput', 'liveRegion'];

  /** @type {HTMLElement[]} All disclosure rows in the DOM (every chunk). */
  #rows = [];
  /** @type {HTMLElement[]} Rows matching the current search term. */
  #matches = [];
  /** Current client-side page (1-based). */
  #page = 1;
  /** Whether the full dataset is loaded and the component has taken over. */
  #ready = false;

  get #sectionId() {
    return this.dataset.sectionId ?? '';
  }

  get #totalCount() {
    return Number.parseInt(this.dataset.totalCount ?? '0', 10) || 0;
  }

  get #serverChunk() {
    return Number.parseInt(this.dataset.serverChunk ?? '250', 10) || 250;
  }

  get #pageSize() {
    return Number.parseInt(this.dataset.clientPageSize ?? '12', 10) || 12;
  }

  get #term() {
    return this.refs.searchInput.value.trim().toLowerCase();
  }

  connectedCallback() {
    super.connectedCallback();
    // Demo mode renders static dummy rows with no data attributes — leave it be.
    if (this.dataset.demo === 'true') return;
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.clientPagination?.removeEventListener('click', this.#handlePaginationClick);
  }

  async #init() {
    const { results } = this.refs;
    if (!results) return; // empty state: nothing to enhance.

    this.#rows = this.#collectRows(results);

    // Swap the no-JS server pagination for in-browser pagination, and take over
    // immediately with the first chunk so the page paints the paginated view
    // (not all 250 server rows) before the remaining chunks load.
    if (this.refs.serverPagination) this.refs.serverPagination.hidden = true;
    if (this.refs.clientPagination) {
      this.refs.clientPagination.hidden = false;
      this.refs.clientPagination.addEventListener('click', this.#handlePaginationClick);
    }
    this.#ready = true;
    this.#apply();

    // Load any remaining chunks once so search/pagination cover the whole
    // catalog, then re-apply with the complete dataset.
    const chunks = Math.ceil(this.#totalCount / this.#serverChunk);
    if (chunks > 1) {
      await this.#loadRemainingChunks(chunks, results);
      this.#rows = this.#collectRows(results);
      this.#apply();
    }
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement[]}
   */
  #collectRows(root) {
    return Array.from(root.querySelectorAll('.sd-product-disclosures__product-row'));
  }

  /**
   * Fetches chunks 2..N once and appends their rows so search/pagination cover
   * the whole catalog. A failed chunk is logged and skipped rather than aborting
   * the rest — partial data beats no data on a compliance page.
   *
   * @param {number} chunks
   * @param {HTMLElement} results
   */
  async #loadRemainingChunks(chunks, results) {
    this.setAttribute('aria-busy', 'true');
    this.#announceLoading();

    // Metaobject lists page with their own URL parameter (e.g. `page_f94af3e4`),
    // not `page`; the section exposes it from `paginate.page_param`.
    const pageParam = results.dataset.pageParam || 'page';

    try {
      for (let page = 2; page <= chunks; page++) {
        const url = new URL(window.location.href);
        url.searchParams.set(pageParam, String(page));

        let html;
        try {
          html = await sectionRenderer.getSectionHTML(this.#sectionId, true, url);
        } catch (error) {
          console.error(`[product-disclosures] failed to load chunk ${page}`, error);
          continue;
        }

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const chunkRows = parsed.querySelectorAll('.sd-product-disclosures__product-row');
        // Append the whole chunk in one DOM mutation (the Component observer
        // rebuilds refs on every childList change, so avoid per-row appends).
        const fragment = document.createDocumentFragment();
        for (const row of chunkRows) {
          fragment.appendChild(document.importNode(row, true));
        }
        results.appendChild(fragment);
      }
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Debounced search-as-you-type. No `inputType` guard: the native search-clear
   * (the input's × button) fires an `input` event with an empty `inputType`, and
   * we want that to reset the filter too.
   */
  handleInput = debounce(() => {
    this.#page = 1;
    this.#apply();
  }, 200);

  /** @param {SubmitEvent} event */
  handleSubmit = (event) => {
    event.preventDefault();
    this.#page = 1;
    this.#apply();
  };

  /** Some browsers fire a `search` event (not `input`) on the native clear (×) and on Enter. */
  handleSearch = () => {
    this.#page = 1;
    this.#apply();
  };

  /** @param {KeyboardEvent} event */
  handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.refs.searchInput.value) return;
    this.refs.searchInput.value = '';
    this.#page = 1;
    this.#apply();
  };

  /** @param {MouseEvent} event */
  #handlePaginationClick = (event) => {
    const link = /** @type {HTMLElement} */ (event.target)?.closest?.('a[data-page]');
    if (!link) return;
    event.preventDefault();
    const page = Number.parseInt(link.getAttribute('data-page') ?? '', 10);
    if (!page) return;
    this.#page = page;
    this.#apply();
    // Scroll to the list (not the whole section), and respect reduced-motion.
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    (this.refs.results ?? this).scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'nearest',
    });
  };

  /**
   * Filters by the current term, paginates the matches, toggles row visibility,
   * rebuilds the pagination, and announces the result count.
   */
  #apply() {
    if (!this.#ready) return;

    const term = this.#term;
    this.#matches = term
      ? this.#rows.filter((row) => {
          const sku = (row.getAttribute('data-sku') ?? '').toLowerCase();
          const name = (row.getAttribute('data-name') ?? '').toLowerCase();
          return sku.includes(term) || name.includes(term);
        })
      : this.#rows;

    const pageCount = Math.max(1, Math.ceil(this.#matches.length / this.#pageSize));
    if (this.#page > pageCount) this.#page = pageCount;

    const start = (this.#page - 1) * this.#pageSize;
    const visible = new Set(this.#matches.slice(start, start + this.#pageSize));

    for (const row of this.#rows) {
      row.hidden = !visible.has(row);
    }

    this.#renderEmptyState(term);
    this.#renderPagination(pageCount);
    this.#announceResults();
  }

  /** @param {string} term */
  #renderEmptyState(term) {
    const { noMatches } = this.refs;
    if (!noMatches) return;
    const show = Boolean(term) && this.#matches.length === 0;
    if (show) {
      const template = noMatches.getAttribute('data-template') ?? '';
      // Function replacer: a string one would treat `$&` etc. in the term as patterns.
      noMatches.textContent = template.replace('__TERMS__', () => this.refs.searchInput.value.trim());
    }
    noMatches.hidden = !show;
  }

  /** @param {number} pageCount */
  #renderPagination(pageCount) {
    const { clientPagination } = this.refs;
    if (!clientPagination) return;

    if (pageCount <= 1) {
      clientPagination.replaceChildren();
      return;
    }

    const nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('aria-label', clientPagination.getAttribute('data-nav-label') ?? 'Pagination');

    const list = document.createElement('ul');
    list.className = 'pagination__list';
    list.setAttribute('role', 'list');

    list.appendChild(this.#arrowItem('prev', this.#page > 1 ? this.#page - 1 : null));
    for (const item of this.#pageWindow(this.#page, pageCount)) {
      list.appendChild(item === 'gap' ? this.#gapItem() : this.#pageItem(item, item === this.#page));
    }
    list.appendChild(this.#arrowItem('next', this.#page < pageCount ? this.#page + 1 : null));

    nav.appendChild(list);
    clientPagination.replaceChildren(nav);
  }

  /**
   * Returns the page numbers to show, with 'gap' markers for elided ranges,
   * e.g. [1, 'gap', 4, 5, 6, 'gap', 20].
   *
   * @param {number} current
   * @param {number} total
   * @returns {(number | 'gap')[]}
   */
  #pageWindow(current, total) {
    /** @type {(number | 'gap')[]} */
    const pages = [1];
    const left = Math.max(2, current - 1);
    const right = Math.min(total - 1, current + 1);

    if (left > 2) pages.push('gap');
    for (let page = left; page <= right; page++) pages.push(page);
    if (right < total - 1) pages.push('gap');
    if (total > 1) pages.push(total);

    return pages;
  }

  /**
   * @param {number} page
   * @param {boolean} isCurrent
   * @returns {HTMLLIElement}
   */
  #pageItem(page, isCurrent) {
    const li = document.createElement('li');
    li.className = 'pagination__item';

    if (isCurrent) {
      const span = document.createElement('span');
      span.className = 'pagination__link pagination__link--page pagination__link--current';
      span.setAttribute('aria-current', 'page');
      span.textContent = String(page);
      li.appendChild(span);
    } else {
      const anchor = document.createElement('a');
      anchor.className = 'pagination__link pagination__link--page';
      anchor.href = '#';
      anchor.setAttribute('data-page', String(page));
      anchor.setAttribute('aria-label', this.#pageLabel(page));
      anchor.textContent = String(page);
      li.appendChild(anchor);
    }

    return li;
  }

  /** @returns {HTMLLIElement} */
  #gapItem() {
    const li = document.createElement('li');
    li.className = 'pagination__item pagination__item--gap';
    const span = document.createElement('span');
    span.className = 'pagination__link pagination__link--gap';
    span.setAttribute('aria-hidden', 'true');
    span.textContent = '…';
    li.appendChild(span);
    return li;
  }

  /**
   * @param {'prev' | 'next'} direction
   * @param {number | null} targetPage
   * @returns {HTMLLIElement}
   */
  #arrowItem(direction, targetPage) {
    const li = document.createElement('li');
    li.className = 'pagination__item';

    const { clientPagination } = this.refs;
    const label =
      direction === 'prev'
        ? clientPagination?.getAttribute('data-prev-label') ?? 'Previous'
        : clientPagination?.getAttribute('data-next-label') ?? 'Next';
    const glyph = direction === 'prev' ? '‹' : '›';

    if (targetPage) {
      const anchor = document.createElement('a');
      anchor.className = 'pagination__link pagination__link--arrow';
      anchor.href = '#';
      anchor.setAttribute('data-page', String(targetPage));
      anchor.setAttribute('aria-label', label);
      anchor.textContent = glyph;
      li.appendChild(anchor);
    } else {
      const span = document.createElement('span');
      span.className = 'pagination__link pagination__link--arrow pagination__link--disabled';
      // Non-interactive and unfocusable, so hide from AT rather than using
      // aria-disabled (invalid on a generic span). aria-current + the live
      // region already convey position.
      span.setAttribute('aria-hidden', 'true');
      span.textContent = glyph;
      li.appendChild(span);
    }

    return li;
  }

  /**
   * @param {number} page
   * @returns {string}
   */
  #pageLabel(page) {
    const template = this.refs.clientPagination?.getAttribute('data-page-label') ?? 'Page __PAGE__';
    return template.replace('__PAGE__', String(page));
  }

  /** Announces the loading state while extra chunks are fetched. */
  #announceLoading() {
    const { liveRegion } = this.refs;
    const template = liveRegion.getAttribute('data-template-loading');
    if (template) liveRegion.textContent = template;
  }

  /**
   * Updates the aria-live region so screen readers hear result-set changes.
   * Reads pre-translated templates from data attributes (rendered via Liquid's
   * `| t`) so the announcement stays in the storefront's locale.
   */
  #announceResults() {
    const { liveRegion, searchInput } = this.refs;
    const term = searchInput.value.trim();
    const template = term
      ? liveRegion.getAttribute('data-template-with-term') ?? ''
      : liveRegion.getAttribute('data-template-count') ?? '';
    liveRegion.textContent = template
      .replace('__COUNT__', String(this.#matches.length))
      .replace('__TERMS__', () => term);
  }
}

if (!customElements.get('sd-product-disclosures')) {
  customElements.define('sd-product-disclosures', ProductDisclosuresComponent);
}
